import React, { useState, useEffect } from 'react';
import { Modal, View, Text, TouchableOpacity, StatusBar, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import { SafeAreaView } from 'react-native-safe-area-context';
import { colors, fontFamily } from '../constants/theme';

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: colors.cream },
    main: { flex: 1, padding: 24, flexDirection: 'column', alignItems: 'center' },
    closeAbs: { position: 'absolute', top: 64, left: 24, zIndex: 40 },
    closeBtn: {
        width: 32,
        height: 32,
        backgroundColor: colors.black,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
    },
    title: {
        fontSize: 24,
        fontFamily: fontFamily.abhayaBold,
        textAlign: 'center',
        marginTop: 80,
        color: colors.brownDark,
    },
    perkBox: {
        marginTop: 48,
        backgroundColor: colors.accentTan2,
        borderRadius: 16,
        padding: 24,
        width: '100%',
        maxWidth: 384,
    },
    perkRow: { flexDirection: 'row', alignItems: 'center' },
    giftIcon: {
        width: 48,
        height: 48,
        backgroundColor: colors.saddle,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 16,
        borderRightWidth: 1,
        borderStyle: 'dashed',
        borderColor: '#D4C4B7',
    },
    perkTextCol: { flex: 1 },
    perkTitle: {
        fontSize: 18,
        fontFamily: fontFamily.abhayaBold,
        color: colors.brownDark,
        marginBottom: 4,
    },
    perkLine: { fontSize: 14, fontFamily: fontFamily.abhaya, color: colors.brownDark, marginBottom: 4 },
    preAct: { marginTop: 48, fontSize: 18, fontFamily: fontFamily.abhayaBold, textAlign: 'center', color: colors.brownDark },
    useBtn: { marginTop: 32, backgroundColor: colors.saddle, borderRadius: 8, paddingHorizontal: 32, paddingVertical: 16 },
    useBtnText: { color: colors.white, fontFamily: fontFamily.abhayaBold, fontSize: 20, textAlign: 'center' },
    postAct: { marginTop: 48, alignItems: 'center' },
    postLabel: { fontSize: 18, fontFamily: fontFamily.abhayaBold, color: colors.brownDark, marginBottom: 8 },
    postBody: { fontSize: 18, fontFamily: fontFamily.abhaya, color: colors.brownDark, marginBottom: 4 },
    timerHuge: { fontSize: 36, fontFamily: fontFamily.abhayaBold, color: colors.brownDark },
});

const BenefitUseModal = ({ isOpen, onClose, benefit, membershipId, selectedRestaurant, getUserMembership }) => {
    const [timer, setTimer] = useState(120); // 2 minutes in seconds
    const [isActivated, setIsActivated] = useState(false);
    const [activationTime, setActivationTime] = useState(null);
    const deleteBenefitMutation = useMutation(api.memberships.deleteBenefit);

    // Get membership data to access tier information
    const membership = getUserMembership ? getUserMembership(selectedRestaurant?.name) : null;

    // Format timestamp as MM.DD.YYYY @HH:MM
    const formatTimestamp = (date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, '0');
        
        return {
            date: `${month}.${day}.${year}`,
            time: `@${displayHours}:${displayMinutes}${ampm}`
        };
    };

    // Format timer as MM:SS
    const formatTimer = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleUsePass = async () => {
        try {
            const deleteParams = { membershipId: membershipId, benefitId: benefit._id };
            const success = await deleteBenefitMutation(deleteParams);
            
            if (success) {
                setIsActivated(true);
                setActivationTime(new Date());
                setTimer(120); // Start 2-minute countdown
            } else {
                console.error("Failed to activate pass.");
                // Could show an error message here
            }
        } catch (error) {
            console.error("Failed to activate pass:", error);
            // Could show an error message here
        }
    };

    const handleClose = () => {
        onClose();
        setIsActivated(false);
        setActivationTime(null);
        setTimer(120);
    };

    // Timer effect
    useEffect(() => {
        if (isActivated && timer > 0) {
            const interval = setInterval(() => {
                setTimer((prevTimer) => {
                    if (prevTimer <= 1) {
                        // Auto-close when timer reaches 0
                        setTimeout(() => handleClose(), 1000);
                        return 0;
                    }
                    return prevTimer - 1;
                });
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [isActivated, timer]);

    if (!isOpen) return null;

    const timestamp = activationTime ? formatTimestamp(activationTime) : null;

    return (
        <Modal
            animationType="fade"
            transparent={false}
            visible={isOpen}
            onRequestClose={handleClose}
        >
            <StatusBar barStyle="dark-content" backgroundColor={colors.cream} />
            <SafeAreaView style={styles.safe}>
                <View style={styles.main}>
                    {/* Header with close button */}
                    <View style={styles.closeAbs}>
                        <TouchableOpacity 
                            onPress={handleClose}
                            style={styles.closeBtn}
                        >
                            <Ionicons name="close" size={20} color="white" />
                        </TouchableOpacity>
                    </View>

                    {/* Title */}
                    <Text style={styles.title}>
                        Show this screen to your server{'\n'}to redeem your perk.
                    </Text>

                    {/* Perk Details Box */}
                    <View style={styles.perkBox}>
                        <View style={styles.perkRow}>
                            {/* Gift Icon */}
                            <View style={styles.giftIcon}>
                                <Ionicons name="gift" size={24} color="white" />
                            </View>
                            
                            {/* Perk Details */}
                            <View style={styles.perkTextCol}>
                                <Text style={styles.perkTitle}>
                                    {benefit.benefit}
                                </Text>
                                <Text style={styles.perkLine}>
                                    Friday • 12am-2am
                                </Text>
                                <Text style={styles.perkLine}>
                                    {selectedRestaurant?.name || 'Seven Spirits'}
                                </Text>
                                <Text style={[styles.perkLine, { marginBottom: 0 }]}>
                                    Loyalty Tiers: {membership?.tier || 'Silver'} +
                                </Text>
                            </View>
                        </View>
                    </View>

                    {/* Pre-activation state */}
                    {!isActivated && (
                        <>
                            <Text style={styles.preAct}>
                                Press 'Use Pass' when ready - it expires in 2 minutes.
                            </Text>
                            <TouchableOpacity
                                onPress={handleUsePass}
                                style={styles.useBtn}
                            >
                                <Text style={styles.useBtnText}>
                                    use pass
                                </Text>
                            </TouchableOpacity>
                        </>
                    )}

                    {/* Post-activation state */}
                    {isActivated && timestamp && (
                        <View style={styles.postAct}>
                            <Text style={styles.postLabel}>
                                Timestamp:
                            </Text>
                            <Text style={styles.postBody}>
                                {timestamp.date}
                            </Text>
                            <Text style={[styles.postBody, { marginBottom: 16 }]}>
                                {timestamp.time}
                            </Text>
                            <Text style={styles.postLabel}>
                                expires in
                            </Text>
                            <Text style={styles.timerHuge}>
                                {formatTimer(timer)}
                            </Text>
                        </View>
                    )}
                </View>
            </SafeAreaView>
        </Modal>
    );
};

export default BenefitUseModal;
